/**
 * usePrefersReducedMotion — single source of truth for "reduce motion".
 *
 * Combines two signals so feature code only has to ask once:
 *   1. OS-level `(prefers-reduced-motion: reduce)` media query — the user
 *      asked their operating system to dampen animations everywhere
 *   2. App-level `useSettingsStore.reduceMotion` — the user toggled the
 *      "低动效" switch inside our Settings page (mirrored to the
 *      `body.reduce-motion` class for CSS — see App.tsx)
 *
 * Either signal alone is enough to suppress motion. We OR them.
 *
 * Why a hook (not a static read):
 *   - The OS preference can change mid-session (macOS/iOS allow live
 *     toggling). We need to react.
 *   - The settings store is reactive already; subscribing keeps the UI
 *     consistent without forcing every consumer to do their own listener.
 *
 * Audit linkage: docs/plans/logic-animation-audit-2026-05-19.md §9.3.
 * Pairs with RULE 17 AP8 (stagger) — when reduced, callers should clamp
 * stagger / disable adjacent-image prefetch / skip non-essential effects.
 */

import { useEffect, useState } from "react";

import { useSettingsStore } from "@/shared/stores/useSettingsStore.js";

/** Media query string for the OS-level reduce-motion preference. */
export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function readOsPreference(): boolean {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia !== "function") return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/**
 * @returns `true` if the user wants animations dampened, either via OS
 * preference or the app's "低动效" setting.
 */
export function usePrefersReducedMotion(): boolean {
  const [osReducedMotion, setOsReducedMotion] = useState<boolean>(readOsPreference);
  const settingReducedMotion = useSettingsStore((state) => state.reduceMotion);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const handler = (event: MediaQueryListEvent) => setOsReducedMotion(event.matches);
    // Initial sync — important if the OS pref changed between SSR and hydrate.
    setOsReducedMotion(mediaQuery.matches);
    // Older Safari only supports the deprecated addListener API.
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    }
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, []);

  return osReducedMotion || settingReducedMotion;
}
